import { ConfigurationRepositoryConfigurationFunctionsService_configurationFunctionServicePostGetSquashedPackage } from '@festina/configuration-general-types/configuration/hooks/ConfigurationRepositoryConfigurationFunctionsService_configurationFunctionServicePostGetSquashedPackage'
import { STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { toast } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'

import { useLazyFetchBranchChangesets } from '../../../../uiModels/api/changesets/useLazyFetchBranchChangesets'
import { useLazyFetchBulkConfigurationPackage } from '../../../../uiModels/api/changesets/useLazyFetchBulkConfigurationPackage'
import type { CommonDownloadOptions, DownloadButtonProps } from '../../types'
import {
  downloadConfigurationFile,
  excludeParameterValueOperationsFromBranchChangesets,
  excludeParameterValueOperationsFromConfigurationPackage,
} from '../helpers'
import { CommonDownloadControls } from './CommonDownloadControls'

const useSquashConfigurationPackage =
  ConfigurationRepositoryConfigurationFunctionsService_configurationFunctionServicePostGetSquashedPackage.useLazyQuery

export const DownloadBulkConfiguration = ({
  branchKey,
}: DownloadButtonProps) => {
  const translate = useTranslate()

  const {
    mutateAsync: fetchBulkConfigurationPackage,
    isPending: isFetchingBulkConfigurationPackage,
  } = useLazyFetchBulkConfigurationPackage()
  const {
    mutateAsync: squashConfigurationPackage,
    isPending: isSquashingConfigurationPackage,
  } = useSquashConfigurationPackage()
  const {
    mutateAsync: fetchBranchChangesets,
    isPending: isFetchingBranchChangesets,
  } = useLazyFetchBranchChangesets()

  const handleExport = async ({
    block,
    preferences,
  }: {
    block: Optional<string>
    preferences: CommonDownloadOptions
  }) => {
    if (block) {
      const result = await fetchBulkConfigurationPackage({
        block,
        branchName: branchKey.name,
        server: branchKey.server,
        shouldIncludeBaseChangesets: preferences.shouldIncludeBaseBranch,
        shouldIncludeExtrinsicParameterValues:
          preferences.shouldIncludeExtrinsicParameterValues,
      })

      const squashedPackage =
        preferences.shouldSquashChangesets && result
          ? await squashConfigurationPackage({ requestBody: result })
          : result

      const downloadedPackage =
        preferences.shouldExcludeParameterValues && squashedPackage
          ? excludeParameterValueOperationsFromConfigurationPackage(
              squashedPackage,
            )
          : squashedPackage

      if (downloadedPackage) {
        // We use the component name as the file name in order to easily support the backend's override scheme approach. This should
        // ideally be changed in the future to something more meaningful, such as componentName + filterFormat.json
        downloadConfigurationFile(downloadedPackage, `${branchKey.server}.json`)
      }
    } else {
      const result = await fetchBranchChangesets({
        server: branchKey.server,
        branchName: branchKey.name,
        block: undefined,
        layer: undefined,
        generalBlocks: [],
        generalLayerKeys: [],
        shouldIncludeBaseBlocks: false,
        shouldIncludeExtrinsicParameterValues:
          preferences.shouldIncludeExtrinsicParameterValues,
        shouldIncludeBaseLayers: false,
        shouldIncludeBaseBranch: preferences.shouldIncludeBaseBranch,
      })

      if (result) {
        const downloadedChangesets = preferences.shouldExcludeParameterValues
          ? excludeParameterValueOperationsFromBranchChangesets(result)
          : result

        // We use the component name as the file name in order to easily support the backend's override scheme approach. This should
        // ideally be changed in the future to something more meaningful, such as componentName + filterFormat.json
        downloadConfigurationFile(
          downloadedChangesets,
          `${branchKey.server}.json`,
        )
      } else {
        toast({
          title: translate('branchesTree.export.bulk.error.noChangesets'),
          variant: 'error',
        })
      }
    }
  }

  const isPreparingDownload =
    isFetchingBulkConfigurationPackage ||
    isSquashingConfigurationPackage ||
    isFetchingBranchChangesets

  const isStandardBranch = branchKey.name === STANDARD_BRANCH

  return (
    <CommonDownloadControls
      branchKey={branchKey}
      isLayerSelectorVisible={false}
      isSquashVisible={!isStandardBranch}
      isAllBlocksOptionVisible={!isStandardBranch}
    >
      {(downloadPreferences) => (
        <Button
          onClick={() =>
            handleExport({
              preferences: downloadPreferences,
              block: downloadPreferences.block,
            })
          }
          isLoading={isPreparingDownload}
          variant="primary"
        >
          {translate('branchesTree.export.bulk.exportLabel')}
        </Button>
      )}
    </CommonDownloadControls>
  )
}
